import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import StaticAdsService from 'services/StaticAdsService';

import ProductAssetPicker from './ProductAssetPicker';
import { bumpStaticActivity } from './activityBus';
import { aspectOptions, aspectLabel } from './staticAspects';
import { defaultPerTake, distinctMixCount, rotateSellingPoints } from './sellingPointRotation';
import { ProductAsset, ProductSummary, StaticFormat } from './types';

// Build an ad from a format. No source ad, and no variation axis — the format IS
// the composition, so "how far from the original" has nothing to refer to.
//
// What varies across runs instead is TAKES (the same brief rolled again), the
// ASPECTS ticked (the same brief composed for each ratio), and optionally a
// different subset of selling points per take — which turns N near-identical
// renders into N creatives that differ by what they actually CLAIM, out of
// inputs the dialog already has.

const MAX_TAKES = 4;

// One paid render. The mix rides along so a RETRY re-queues exactly what
// failed — redrawing it could hand a retried take the same mix an
// already-queued run is rendering.
type StructuredRun = { aspectRatio: string; take: number; sellingPoints?: string[] };

export default function StructuredGenerateDialog({ open, format, service, onClose, onQueued }: {
  open: boolean;
  format: StaticFormat | null;
  service: StaticAdsService;
  onClose: () => void;
  onQueued?: (count: number) => void;
}) {
  const [products, setProducts] = useState<ProductSummary[] | null>(null);
  const [productsError, setProductsError] = useState(false);
  const [selected, setSelected] = useState<ProductSummary | null>(null);

  const [assets, setAssets] = useState<ProductAsset[] | null>(null);
  const [assetsError, setAssetsError] = useState(false);
  const [chosenAssetIds, setChosenAssetIds] = useState<Set<string>>(new Set());

  const [points, setPoints] = useState<string[] | null>(null);
  // A failed selling-points load must not read as "this product has no points".
  const [pointsError, setPointsError] = useState(false);
  const [chosenPoints, setChosenPoints] = useState<Set<string>>(new Set());
  const [brief, setBrief] = useState('');
  const [aspects, setAspects] = useState<Set<string>>(new Set(['1:1']));
  const [takes, setTakes] = useState(1);
  const [rotatePoints, setRotatePoints] = useState(false);
  const [perTake, setPerTake] = useState(1);

  const [step, setStep] = useState<'configure' | 'preview'>('configure');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [previewSheet, setPreviewSheet] = useState<Record<string, unknown> | null>(null);
  const [previewUnfilled, setPreviewUnfilled] = useState<string[]>([]);
  const [previewNeedsPhoto, setPreviewNeedsPhoto] = useState(false);
  const [grounding, setGrounding] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After a partial submit, the exact runs that never queued — re-running the
  // whole batch would re-bill the runs that already landed.
  const [pending, setPending] = useState<StructuredRun[] | null>(null);
  const [queuedSoFar, setQueuedSoFar] = useState(0);
  // The group the already-queued runs joined; a retry must join the same one.
  const [groupId, setGroupId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open || !format) return;
    setStep('configure');
    setPreviewPrompt('');
    setPreviewSheet(null);
    setPreviewUnfilled([]);
    setPreviewNeedsPhoto(false);
    setGrounding(null);
    setError(null);
    setPending(null);
    setQueuedSoFar(0);
    setGroupId(undefined);
    setTakes(1);
    setRotatePoints(false);
    setBrief('');
    // The format declares its ratio — the first place defaultAspect is read.
    // Seeded as the only selection; more can be ticked to render the same brief
    // at each one.
    setAspects(new Set([format.defaultAspect]));
    setPerTake(1);
    setProducts(null);
    setProductsError(false);
    void service.listProducts().then((resp) => {
      if (resp) setProducts(resp.products || []);
      else setProductsError(true);
    });
  }, [open, format, service]);

  // Responses are sequence-guarded: switching products while a slow response
  // is in flight must not seed the OLD product's photos or points under the
  // new one — those are the references a paid render would use.
  const assetsSeq = useRef(0);
  const loadAssets = (product: ProductSummary) => {
    const seq = ++assetsSeq.current;
    setAssets(null);
    setAssetsError(false);
    setChosenAssetIds(new Set());
    void service.listProductAssets(Number(product.id)).then((resp) => {
      if (seq !== assetsSeq.current) return;
      if (resp) {
        const list = resp.assets || [];
        setAssets(list);
        setChosenAssetIds(new Set(list.map(a => a.assetId)));
      } else setAssetsError(true);
    });
  };

  const pointsSeq = useRef(0);
  const loadPoints = (product: ProductSummary) => {
    const seq = ++pointsSeq.current;
    setPoints(null);
    setPointsError(false);
    void service.getSellingPoints(Number(product.id)).then((resp) => {
      if (seq !== pointsSeq.current) return;
      // null is the service's 404: no brief yet, so genuinely no points.
      setPoints(resp ? (resp.sellingPoints || []).slice(0, 12) : []);
    }).catch(() => {
      if (seq !== pointsSeq.current) return;
      setPointsError(true);
    });
  };

  useEffect(() => {
    // Ticked points always belong to the product picked NOW — product A's
    // claims must never ride along on product B's render.
    setChosenPoints(new Set());
    if (!selected) {
      setAssets(null);
      setAssetsError(false);
      setPoints(null);
      setPointsError(false);
      return undefined;
    }
    loadAssets(selected);
    loadPoints(selected);
    // Deselecting bumps the guards too, so a late response cannot restore a
    // product that is no longer picked.
    return () => { assetsSeq.current += 1; pointsSeq.current += 1; };
  }, [selected, service]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = useMemo(() => [...chosenPoints], [chosenPoints]);
  const aspectList = useMemo(
    () => aspectOptions(format ? format.defaultAspect : null).filter(a => aspects.has(a)),
    [aspects, format],
  );
  const canRotate = chosen.length > 1 && takes > 1;

  // Every aspect gets every take: two ratios and three takes is six creatives,
  // and the number has to be on screen before the click, not on the invoice.
  const totalRuns = takes * Math.max(1, aspectList.length);

  const rotationActive = rotatePoints && canRotate;
  const mixCount = distinctMixCount(chosen.length, perTake);
  // Every run is built from the identical sheet exactly when one ratio is
  // ticked and rotation isn't varying the points — only then can an edited
  // preview prompt honestly apply to all runs.
  const previewCoversAllRuns = aspectList.length === 1 && !(rotationActive && mixCount > 1);

  // Keep the per-take size inside what the selection can actually supply, so the
  // field never asks for more points than exist.
  useEffect(() => {
    setPerTake(prev => Math.min(Math.max(1, prev), Math.max(1, chosen.length)));
  }, [chosen.length]);

  const loadPreview = async () => {
    if (!format || !selected) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const resp = await service.structuredPreview({
        formatKey: format.key,
        productId: String(selected.id),
        sellingPoints: chosen.length ? chosen : undefined,
        brief: brief.trim() || undefined,
        // The preview is one prompt, so it previews the first ticked ratio; the
        // others reuse the same sheet at a different size.
        aspectRatio: aspectList[0] || format.defaultAspect,
        hasProductReferences: !!assets && chosenAssetIds.size > 0,
      });
      setPreviewPrompt(resp.prompt);
      setPreviewSheet(resp.buildSheet || null);
      setPreviewUnfilled(resp.unfilledSlots || []);
      setPreviewNeedsPhoto(!!resp.needsProductPhoto);
      setGrounding(resp.groundingWarning || null);
      setStep('preview');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const submit = async () => {
    if (!format || !selected) return;
    setConfirmOpen(false);
    setBusy(true);
    setError(null);

    let targets = pending;
    if (!targets) {
      // Subsets are drawn ONCE for the whole submit, not per request: the
      // rotation guarantees the takes differ from each other, which it cannot
      // do if each request samples independently. Each take claims a different
      // subset, so the extra renders are worth what they cost. The same subsets
      // repeat across aspects on purpose: that is the same creative at another
      // size, not a different creative.
      const subsets = rotationActive ? rotateSellingPoints(chosen, takes, perTake) : null;
      targets = aspectList.flatMap(
        aspectRatio => Array.from({ length: takes }, (_, take): StructuredRun => ({
          aspectRatio,
          take,
          sellingPoints: subsets ? subsets[take] : (chosen.length ? chosen : undefined),
        })),
      );
    }

    // One group when there is more than one take: they are the same brief in the
    // same format, which is exactly what a stack is for. If the first attempt
    // queued its runs UNGROUPED (the group call failed), a retry stays
    // ungrouped with them rather than forking a group the others aren't in.
    let group = groupId;
    if (!group && !queuedSoFar && targets.length > 1) {
      try {
        const g = await service.createGenerationGroup();
        group = g.groupId;
        setGroupId(group);
      } catch {
        // Best-effort: ungrouped creatives are better than no creatives.
      }
    }

    const failures: string[] = [];
    const stillPending: StructuredRun[] = [];
    let queued = queuedSoFar;
    for (const run of targets) {
      try {
        await service.createStructuredGeneration({
          formatKey: format.key,
          productId: String(selected.id),
          assetIds: assets ? [...chosenAssetIds] : undefined,
          sellingPoints: run.sellingPoints,
          brief: brief.trim() || undefined,
          aspectRatio: run.aspectRatio,
          groupId: group,
          ...(step === 'preview' && previewCoversAllRuns && previewPrompt.trim()
            ? { promptOverride: previewPrompt.trim(), buildSheet: previewSheet || undefined }
            : {}),
        });
        queued += 1;
      } catch (err: any) {
        failures.push(err?.response?.data?.error || err?.message || 'unknown error');
        stillPending.push(run);
      }
    }

    setQueuedSoFar(queued);
    setPending(stillPending.length ? stillPending : null);
    setBusy(false);
    if (failures.length) {
      // Whatever queued is real and already spending, so polling has to start
      // even on a partial submit — hiding it is what invites a second paid click.
      if (queued) bumpStaticActivity();
      setError(`${failures.length} of ${targets.length} could not be queued: ${failures[0]}`
        + (queued ? ` — ${queued} ${queued > 1 ? 'are' : 'is'} already queued and running; Retry sends only the ${stillPending.length} that failed.` : '')
        + ' To change the setup instead, close and start over.');
      return;
    }
    bumpStaticActivity();
    if (onQueued) onQueued(queued);
    onClose();
  };

  if (!format) return null;

  const ready = !!selected && aspectList.length > 0;

  return (
    <>
      <Dialog open={open} onClose={busy || previewLoading ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {format.label}
          <Typography variant="body2" color="text.secondary">{format.description}</Typography>
        </DialogTitle>

        {step === 'preview' ? (
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {previewCoversAllRuns
                ? `This exact text goes to the image model. Edit freely — your edits apply to ${
                  totalRuns === 1 ? 'this generation only' : `all ${totalRuns} runs of this generation`}.`
                : `Preview of the first run's prompt only. Each of the ${totalRuns} runs builds its own`
                  + `${rotationActive && mixCount > 1 ? ' around its own mix of selling points' : ''}`
                  + `${aspectList.length > 1 ? ' at its own ratio' : ''}, so this text cannot be`
                  + ' edited here — go back and change the setup instead.'}
            </Typography>
            {previewUnfilled.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                The sheet left {previewUnfilled.length} part
                {previewUnfilled.length === 1 ? '' : 's'} of the format blank
                ({previewUnfilled.join(', ')}). The render still costs money but will not fill the
                format properly — add direction below, or edit the prompt.
              </Alert>
            )}
            {previewNeedsPhoto && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                This format expects a product photo. Without one attached the model will invent a
                product.
              </Alert>
            )}
            <TextField
              fullWidth multiline minRows={12} maxRows={24} value={previewPrompt}
              onChange={e => setPreviewPrompt(e.target.value)}
              InputProps={{
                // An edit that would be silently dropped is worse than no edit:
                // when the runs each build their own prompt, this is a preview,
                // not an editor.
                readOnly: !previewCoversAllRuns,
                sx: { fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.5 },
              }}
            />
          </DialogContent>
        ) : (
          <DialogContent dividers>
            <Stack spacing={2}>
              {grounding && <Alert severity="warning">{grounding}</Alert>}

              {productsError && (
                <Alert severity="error">
                  Couldn&apos;t load the product catalog — a format needs a product to advertise.
                </Alert>
              )}

              <Autocomplete
                size="small"
                options={products || []}
                value={selected}
                loading={!products && !productsError}
                onChange={(_e, next) => setSelected(next)}
                getOptionLabel={p => p.title}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={params => (
                  <TextField {...params} label="Product" placeholder="What is this ad for?"
                    helperText="Required — the format supplies the structure, the product supplies the subject" />
                )}
              />

              {selected && assets && assets.length > 0 && (
                <ProductAssetPicker
                  assets={assets}
                  chosen={chosenAssetIds}
                  onToggle={(assetId) => {
                    setChosenAssetIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(assetId)) next.delete(assetId); else next.add(assetId);
                      return next;
                    });
                  }}
                  caption="Reference photos — the product's appearance comes only from these"
                />
              )}
              {selected && assetsError && (
                <Alert
                  severity="warning"
                  action={(
                    <Button color="inherit" size="small" onClick={() => loadAssets(selected)}>
                      Retry
                    </Button>
                  )}
                >
                  Couldn&apos;t load this product&apos;s photos. Generating without them means the
                  model invents the product&apos;s appearance.
                </Alert>
              )}

              {selected && pointsError && (
                <Alert
                  severity="error"
                  action={(
                    <Button color="inherit" size="small" onClick={() => loadPoints(selected)}>
                      Retry
                    </Button>
                  )}
                >
                  Couldn&apos;t load this product&apos;s selling points — that&apos;s a failed load,
                  not a product without any. Generating now features none of them.
                </Alert>
              )}

              {points && points.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Selling points to feature
                  </Typography>
                  <Stack>
                    {points.map(point => (
                      <FormControlLabel
                        key={point}
                        control={(
                          <Checkbox
                            size="small"
                            checked={chosenPoints.has(point)}
                            onChange={() => setChosenPoints((prev) => {
                              const next = new Set(prev);
                              if (next.has(point)) next.delete(point); else next.add(point);
                              return next;
                            })}
                          />
                        )}
                        label={<Typography variant="body2">{point}</Typography>}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <TextField
                size="small" label="Creative direction" value={brief} fullWidth multiline minRows={2}
                onChange={e => setBrief(e.target.value)}
                helperText="With no source ad, this and the selling points are the only direction the model gets"
              />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Aspect ratios — the same brief is composed for each one you tick
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {aspectOptions(format.defaultAspect).map(a => (
                    <FormControlLabel
                      key={a}
                      control={(
                        <Checkbox
                          size="small"
                          checked={aspects.has(a)}
                          onChange={() => setAspects((prev) => {
                            const next = new Set(prev);
                            if (next.has(a)) next.delete(a); else next.add(a);
                            return next;
                          })}
                        />
                      )}
                      label={<Typography variant="body2">{aspectLabel(a)}</Typography>}
                    />
                  ))}
                </Stack>
                {!aspectList.length && (
                  <Typography variant="caption" color="error">
                    Tick at least one ratio.
                  </Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <TextField
                  select size="small" label="Takes per ratio" value={takes} sx={{ flex: 1 }}
                  onChange={e => setTakes(Number(e.target.value))}
                  helperText={aspectList.length > 1
                    ? `${takes} × ${aspectList.length} ratios = ${totalRuns} paid renders`
                    : `${totalRuns} paid render${totalRuns > 1 ? 's' : ''}`}
                >
                  {Array.from({ length: MAX_TAKES }, (_, i) => i + 1).map(n => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </TextField>
                {rotationActive && (
                  <TextField
                    select size="small" label="Points per take" value={perTake} sx={{ flex: 1 }}
                    onChange={e => setPerTake(Number(e.target.value))}
                    helperText={`from the ${chosen.length} ticked`}
                  >
                    {Array.from({ length: Math.max(1, chosen.length) }, (_, i) => i + 1).map(n => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>

              {takes > 1 && (
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small" checked={rotatePoints && canRotate} disabled={!canRotate}
                      onChange={(e) => {
                        setRotatePoints(e.target.checked);
                        // Sized from the selection the first time it is turned
                        // on: one point out of a dozen is a thin ad.
                        if (e.target.checked) setPerTake(defaultPerTake(chosen.length));
                      }}
                    />
                  )}
                  label={(
                    <Typography variant="body2">
                      Give each take a different mix of selling points
                      {!canRotate && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}— needs two or more selling points ticked
                        </Typography>
                      )}
                    </Typography>
                  )}
                />
              )}

              {rotationActive && (
                <Alert severity="info">
                  {mixCount >= takes
                    ? `Each take features ${perTake} of your ${chosen.length} points, drawn so no `
                      + 'two takes get the same mix'
                    : mixCount === 1
                      ? `Every take features all ${chosen.length} ticked points — that leaves only `
                        + "one possible mix, so the takes differ only by the model's own randomness"
                      : `Each take features ${perTake} of your ${chosen.length} points, but only `
                        + `${mixCount} different mixes exist — later takes repeat them in order `
                        + `(take ${mixCount + 1} matches take 1)`}
                  {aspectList.length > 1 ? ', and each ratio repeats those same takes' : ''}.
                </Alert>
              )}

              {takes > 1 && !rotationActive && (
                <Alert severity="info">
                  {takes} takes of the same brief. They will differ only by the model&apos;s own
                  randomness — tick the box above to vary what each one claims.
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary">
                This format renders its text into the image; the copy cannot be edited afterwards.
              </Typography>
            </Stack>
          </DialogContent>
        )}

        <DialogActions>
          {step === 'preview' ? (
            <>
              <Button onClick={() => setStep('configure')} disabled={busy}>Back</Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained" disabled={busy || !previewPrompt.trim()}
                onClick={() => (!pending && totalRuns > 1 ? setConfirmOpen(true) : void submit())}
              >
                {busy ? 'Queueing…'
                  : pending ? `Retry ${pending.length} that failed`
                    : `Generate ${totalRuns} creative${totalRuns > 1 ? 's' : ''}`}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose} disabled={busy || previewLoading}>Cancel</Button>
              <Box sx={{ flex: 1 }} />
              {/* The only way forward: nothing renders from a prompt nobody
                  read. Free, too — the worker skips stage 2 when an approved
                  prompt arrives, so the bill moves earlier rather than up. */}
              <Button
                variant="contained"
                onClick={loadPreview}
                disabled={busy || previewLoading || !ready}
                startIcon={previewLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {previewLoading ? 'Writing the prompt…'
                  : pending ? `Review ${pending.length} that failed`
                    : `Review the prompt${totalRuns > 1 ? ` for ${totalRuns} runs` : ''}`}
              </Button>
            </>
          )}
        </DialogActions>
        {/* Outside the step conditional: a failed submit from the PREVIEW step
            must be just as visible as one from configure. */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2, mt: 0 }}>
            {error}
          </Alert>
        )}
      </Dialog>

      {/* More than one paid render gets an explicit yes. The count is on the
          button too, but a number on a button is easy to click past. */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate {totalRuns} creatives?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {totalRuns} images in the <strong>{format.label}</strong> format for{' '}
            <strong>{selected?.title}</strong>. Each one is billed whether or not you keep it.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {takes} take{takes > 1 ? 's' : ''} at {aspectList.map(aspectLabel).join(' and ')}
            {rotationActive
              ? (mixCount >= takes
                ? `, each take claiming a different ${perTake} of your ${chosen.length} selling points.`
                : mixCount === 1
                  ? `, every take claiming all ${chosen.length} selling points.`
                  : `, cycling the ${mixCount} possible mixes of ${perTake} selling points — later takes repeat earlier ones.`)
              : '.'}
          </Typography>
          {step === 'preview' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {previewCoversAllRuns
                ? 'Every run uses the prompt exactly as you approved it.'
                : 'Each run writes its own prompt — the preview showed the first run only.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void submit()}>Generate</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
